import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Settings, Users, LogOut } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { envOf, tint } from "@/lib/environments";
import { SpaceAvatar, UserAvatar } from "@/components/Avatars";
import { EnvBadge } from "@/components/EnvBadge";
import { EnvironmentPickerDialog } from "@/components/EnvironmentPickerDialog";
import { GuidedTour } from "@/components/GuidedTour";
import { ProjectsSection } from "@/components/project/ProjectsSection";
import { SpaceTabs } from "@/components/SpaceSection";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function SpacePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const { data: ws, isLoading, error } = useQuery({
    queryKey: ["workspace", id],
    queryFn: async () => (await api.get(`/workspaces/${id}`)).data,
    retry: false,
  });
  const { data: members = [] } = useQuery({
    queryKey: ["members", id],
    queryFn: async () => (await api.get(`/workspaces/${id}/members`)).data,
    enabled: !!ws,
  });

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center space-y-3">
        <p className="text-3xl">🔒</p>
        <p className="font-heading font-semibold text-lg">{apiError(error)}</p>
        <Button variant="outline" onClick={() => navigate("/")}>Volver a Inicio</Button>
      </div>
    );
  }

  if (isLoading || !ws) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 space-y-4">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  const isAdmin = ["owner", "admin"].includes(ws.my_role);
  const myMember = members.find((m) => m.profile_id === user?.id);

  const leaveSpace = async () => {
    try {
      await api.delete(`/workspaces/${id}/members/${myMember.id}`);
      toast.success("Has abandonado el Espacio");
      qc.invalidateQueries({ queryKey: ["home"] });
      navigate("/");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-10 space-y-8" data-testid="space-page">
      <div
        className="flex flex-col sm:flex-row sm:items-center gap-4 animate-fade-up rounded-3xl p-5 sm:p-6"
        style={{ backgroundColor: tint(envOf(ws).color, 0.14) }}
        data-testid="space-header-band"
      >
        <SpaceAvatar icon={ws.icon} color={ws.color} size="xl" />
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight truncate" data-testid="space-name">{ws.name}</h1>
          {ws.description && <p className="text-muted-foreground text-sm mt-1">{ws.description}</p>}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <EnvBadge ws={ws} />
            <Badge variant="secondary" data-testid="space-my-role">{ws.my_role_label}</Badge>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" /> {ws.member_count} {ws.member_count === 1 ? "miembro" : "miembros"}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button asChild variant="outline" data-testid="space-settings-btn">
              <Link to={`/space/${id}/settings`}><Settings className="h-4 w-4 mr-2" /> Administrar</Link>
            </Button>
          )}
          {ws.my_role !== "owner" && myMember && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" className="text-destructive" data-testid="space-leave-btn">
                  <LogOut className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Abandonar este Espacio?</AlertDialogTitle>
                  <AlertDialogDescription>Perderás el acceso a {ws.name}. Tu historial de actividad se conservará.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction data-testid="space-leave-confirm-btn" onClick={leaveSpace}>Abandonar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <SpaceTabs workspaceId={id} />

      <ProjectsSection workspaceId={id} canCreate={ws.my_role !== "guest"} />

      <div className="grid md:grid-cols-2 gap-6 animate-fade-up">
        <Card className="p-6 space-y-4" data-testid="space-members-card">
          <div className="flex items-center justify-between">
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Miembros</h2>
            {isAdmin && (
              <Button asChild variant="ghost" size="sm">
                <Link to={`/space/${id}/settings?tab=members`} data-testid="space-manage-members-link">Gestionar</Link>
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {members.slice(0, 6).map((m) => (
              <div key={m.id} className="flex items-center gap-3">
                <UserAvatar name={m.name} color={m.avatar_color} src={m.avatar_url} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.name}</p>
                </div>
                <Badge variant="outline" className="font-normal text-xs">{m.role_label}</Badge>
                {m.status === "suspended" && <Badge variant="destructive" className="text-xs">Suspendido</Badge>}
              </div>
            ))}
            {members.length > 6 && (
              <p className="text-xs text-muted-foreground">y {members.length - 6} más...</p>
            )}
          </div>
        </Card>
      </div>

      <HuchasCard workspaceId={id} />

      <TeamLoadCard workspaceId={id} />

      <EnvironmentPickerDialog ws={ws} />

      {(ws.environment || !["owner", "admin"].includes(ws.my_role)) && (
        <GuidedTour
          flag="tour_done"
          steps={[
            { target: '[data-testid="space-header-band"]', emoji: "👋", title: "¡Bienvenido a tu Espacio!", text: `Este es el centro de mando de "${ws.name}": su entorno, miembros y todo lo que ocurre, en un vistazo.` },
            { target: '[data-testid="space-projects-card"]', emoji: "📋", title: "Proyectos y vista Gantt", text: "Crea proyectos y organiza sus tareas en vistas Lista, Kanban, Calendario y Gantt, ideal para planificar en el tiempo. Ábrelos desde aquí." },
            { target: '[data-testid="ai-assistant-fab"]', emoji: "✨", title: "Tu asistente IA", text: "Pídele lo que necesites: crear tareas, resumir la actividad del espacio o planificar la semana. Está siempre en esta esquina." },
            { target: '[data-testid="space-members-card"]', emoji: "👥", title: "Invita a los tuyos", text: "Un espacio se disfruta acompañado: invita a tu familia, equipo o club y asigna roles a cada uno." },
            { emoji: "🚀", title: "¡Todo listo!", text: "Explora a tu ritmo. Consejo: la vista Gantt y el asistente IA son las joyas de Nuxxum Premium, y puedes probarlas gratis durante 14 días." },
          ]}
        />
      )}
    </div>
  );
}

const TeamLoadCard = ({ workspaceId }) => {
  const { data = [] } = useQuery({
    queryKey: ["team-load", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/tasks-by-member`)).data,
  });
  if (!data.length) return null;
  return (
    <Card className="p-6 space-y-4 animate-fade-up" data-testid="space-team-load-card">
      <div className="flex items-center gap-2">
        <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Carga del equipo</h2>
        <span className="text-[11px] text-muted-foreground ml-auto">Tareas abiertas por responsable</span>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
          <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} tickFormatter={(v) => v.split(" ")[0]} />
          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
          <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="pending" name="Pendientes" stackId="a" fill="#9AA0A6" radius={[0, 0, 0, 0]} />
          <Bar dataKey="in_progress" name="En curso" stackId="a" fill="#1A73E8" />
          <Bar dataKey="overdue" name="Fuera de plazo" stackId="a" fill="#D93025" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};

const HuchasCard = ({ workspaceId }) => {
  const { data: huchas = [] } = useQuery({
    queryKey: ["huchas", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/huchas`)).data,
  });
  if (huchas.length === 0) return null;
  return (
    <Card className="p-6 space-y-4 animate-fade-up" data-testid="space-huchas-card">
      <div className="flex items-center gap-2">
        <span className="text-lg">🐷</span>
        <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Huchas</h2>
        <span className="text-[11px] text-muted-foreground ml-auto">Recompensas ganadas completando tareas</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {huchas.map((h) => (
          <div key={`${h.profile_id}-${h.currency}`} data-testid={`hucha-${h.profile_id}`}
            className="flex items-center gap-3 rounded-lg border p-3 bg-secondary/30">
            <UserAvatar name={h.name} color={h.avatar_color} src={h.avatar_url} size="md" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">Hucha de {h.name}</p>
              <p className="text-[11px] text-muted-foreground">{h.count} {h.count === 1 ? "recompensa" : "recompensas"}</p>
            </div>
            <span className="font-heading font-bold text-emerald-600 dark:text-emerald-400">
              {h.total.toFixed(2).replace(".", ",")} {h.currency === "EUR" ? "€" : h.currency}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
};
